import { z } from 'zod';
import { AdminPageQuerySchema, adminPage } from './page';

/**
 * Admin R2 media manager. **Web-only.** Lets a moderator see how much is stored
 * (count / per-object size / totals, by kind) and delete objects to keep the R2
 * free tier under control. It exposes **metadata only** — there is deliberately
 * no route, URL, or field anywhere that returns image bytes.
 */

/** R2 free tier storage ceiling, for the "used vs free" bar in the UI. */
export const R2_FREE_TIER_BYTES = 10 * 1024 * 1024 * 1024;

export const MediaKindSchema = z.enum(['whisper', 'pro']);
export type MediaKind = z.infer<typeof MediaKindSchema>;

export const AdminMediaObjectSchema = z.object({
  key: z.string(),
  kind: MediaKindSchema,
  refId: z.string().nullable(),
  size: z.number().int().nonnegative(),
  contentType: z.string(),
  createdAt: z.iso.datetime(),
  deletedAt: z.iso.datetime().nullable(),
});
export type AdminMediaObject = z.infer<typeof AdminMediaObjectSchema>;

export const AdminMediaQuerySchema = AdminPageQuerySchema.extend({
  filter: z.enum(['all', 'whisper', 'pro', 'deleted']).default('all'),
});
export type AdminMediaQuery = z.infer<typeof AdminMediaQuerySchema>;

const MediaBucketStatSchema = z.object({
  count: z.number().int().nonnegative(),
  bytes: z.number().int().nonnegative(),
});

export const AdminMediaResponseSchema = adminPage(AdminMediaObjectSchema).extend({
  summary: z.object({
    count: z.number().int().nonnegative(),
    totalBytes: z.number().int().nonnegative(),
    byKind: z.record(z.string(), MediaBucketStatSchema),
  }),
});
export type AdminMediaResponse = z.infer<typeof AdminMediaResponseSchema>;

export const AdminMediaDeleteSchema = z.object({
  reason: z.string().min(3).max(500),
});
export type AdminMediaDelete = z.infer<typeof AdminMediaDeleteSchema>;

/** Bulk cleanup to reclaim R2 space. Deletes up to `BULK_MEDIA_LIMIT` matching
 * objects per call; the response says how many are left so the admin can repeat. */
export const BULK_MEDIA_LIMIT = 200;

export const AdminMediaBulkDeleteSchema = z.object({
  olderThanDays: z.number().int().min(1).max(3650).optional(),
  kind: MediaKindSchema.optional(),
  reason: z.string().min(3).max(500),
});
export type AdminMediaBulkDelete = z.infer<typeof AdminMediaBulkDeleteSchema>;

export const AdminMediaBulkDeleteResponseSchema = z.object({
  deleted: z.number().int().nonnegative(),
  remaining: z.number().int().nonnegative(),
});
export type AdminMediaBulkDeleteResponse = z.infer<typeof AdminMediaBulkDeleteResponseSchema>;
